import { ErrorCode } from '@constants/errorCodes';
import { getApiErrorMessage } from '@helpers/apiError';
import { ApiError } from '@types';

type SaveLeadMode = 'edit' | 'create' | 'delete';

export type SaveLeadErrorOptions = {
  updateContext?: 'assignee' | 'selfAssign';
};

const LEAD_ERROR_MESSAGES = {
  forbidden: {
    edit: "You don't have permission to update this lead.",
    create: "You don't have permission to create a lead.",
    delete: "You don't have permission to delete this lead.",
    assignee: "You don't have permission to update assignee.",
    // Claims on unassigned leads are only refused once another agent has it.
    selfAssign: 'This lead was already assigned to someone else.'
  },
  default: {
    edit: 'Failed to update lead. Please try again.',
    create: 'Failed to create lead. Please try again.',
    delete: 'Failed to delete lead. Please try again.',
    assignee: 'Failed to update assignee. Please try again.',
    selfAssign: 'Failed to assign lead. Please try again.'
  }
} as const;

function resolveForbiddenTier(error: unknown): 'forbidden' | 'default' {
  const apiError = error as ApiError;
  return apiError?.data?.errorCode === ErrorCode.ERR_FORBIDDEN
    ? 'forbidden'
    : 'default';
}

export default function getSaveLeadErrorMessage(
  error: unknown,
  isEditMode: boolean,
  options?: SaveLeadErrorOptions
): string {
  const mode: SaveLeadMode = isEditMode ? 'edit' : 'create';
  const tier = resolveForbiddenTier(error);
  const fallback = LEAD_ERROR_MESSAGES[tier][options?.updateContext ?? mode];

  if (tier === 'forbidden') return fallback;

  return getApiErrorMessage(error) || fallback;
}

export function getDeleteLeadErrorMessage(error: unknown): string {
  const tier = resolveForbiddenTier(error);
  const fallback = LEAD_ERROR_MESSAGES[tier].delete;

  if (tier === 'forbidden') return fallback;

  return getApiErrorMessage(error) || fallback;
}
